import memoize from 'memoize-one'

import React, { useEffect } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { loadArtist } from '@disco/state-management/actions/artist-actions'
import api from '@disco/api/server'
import {
  ARTISTS_LOAD,
  ARTISTS_LOAD_FAIL,
  ARTISTS_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import { isArtistExists } from '@disco/state-management/selectors/artists'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
let _abortController: AbortController | null | undefined = null

const memoizeCb = async (input: string) => {
  if (_abortController) {
    _abortController.abort()
  }

  _abortController = new AbortController()
  const response = await api.getArtists(
    input,
    undefined,
    _abortController.signal,
  )
  return response.results
}

// memoize function to not trigger api call when input doesn't change
let fetchArtists = memoize(memoizeCb)
type Props = {
  onChange: (...args: Array<any>) => any
  getOptions: (...args: Array<any>) => any
  value: Record<string, any> | null | undefined
  checkForApiUpdates?: boolean
  disabled?: boolean
}

/**
 * Artist selection dropdown component with async search functionality
 * Loads and caches artist data from API with memoization for performance
 * @param onChange - Callback when artist selection changes
 * @param getOptions - Function to format artist options for dropdown
 * @param value - Currently selected artist value
 * @param checkForApiUpdates - Whether to refresh cached data on mount
 * @param disabled - Whether the dropdown is disabled
 */
const ArtistSelection = ({
  onChange,
  getOptions,
  value,
  checkForApiUpdates,
  disabled,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const artists = useSelector(state => state.artists.items)
  const artistExists = useSelector(state => isArtistExists(state, value?.value))
  useEffect(() => {
    if (checkForApiUpdates) {
      // memoize function to not trigger api call when input doesn't change.
      // After opening and closing the ArtistSelection component, the data from the api can change,
      // so we always make a request at the first render to get the actual data.
      fetchArtists = memoize(memoizeCb)
    }
  }, [])
  useEffect(() => {
    const artistId = value?.value

    // load init artist
    if (!artists?.[artistId] && artistId) {
      dispatch(loadArtist(artistId))
    }
  }, [value, artists])
  const loadArtistOptions = useDebouncedCallback(async (input, callback) => {
    let artists: Awaited<ReturnType<typeof fetchArtists>> = []
    dispatch({
      type: ARTISTS_LOAD,
    })

    try {
      artists = await fetchArtists(input)
      dispatch({
        type: ARTISTS_LOAD_SUCCESS,
        response: {
          results: artists,
        },
      })
    } catch (_) {
      dispatch({
        type: ARTISTS_LOAD_FAIL,
      })
    }

    return getOptions({
      input,
      callback,
      artists,
    })
  }, 300)
  return (
    <>
      <Label caption="Choose an artist" />
      <CustomDropdown
        async
        loadOptions={(input, callback) => {
          if (disabled) return null
          loadArtistOptions(input, callback)
        }}
        value={artistExists ? value : undefined}
        onChange={onChange}
        disabled={disabled}
      />
    </>
  )
}

export default ArtistSelection
